'use client';

import { useEffect, useRef } from 'react';
import { Message } from '@/types/messages';
import MessageBubble from './MessageBubble';

interface MessageListProps {
  messages: Message[];
  currentUserId: string;
  currentUserRole?: string;
  onDeleteMessage?: (messageId: string) => void;
}

export default function MessageList({ messages, currentUserId, currentUserRole, onDeleteMessage }: MessageListProps) {
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const scrollToBottom = () => {
    setTimeout(() => {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, 100);
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  if (messages.length === 0) {
    return (
      <div 
        className="flex-1 flex items-center justify-center"
        style={{ color: '#A1A1AA' }}
      >
        <div className="text-center">
          <svg 
            className="w-16 h-16 mx-auto mb-4 opacity-40" 
            fill="none" 
            stroke="currentColor" 
            viewBox="0 0 24 24"
          >
            <path 
              strokeLinecap="round" 
              strokeLinejoin="round" 
              strokeWidth={1.5} 
              d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" 
            />
          </svg>
          <p className="text-lg font-medium">No messages yet</p>
          <p className="text-sm mt-1 opacity-70">Start the conversation!</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex-1 overflow-y-auto px-4 py-6">
      {messages.map((message) => {
        // Determine if this is the current user's message by comparing sender_role
        // If currentUserRole is provided, use it; otherwise fall back to user_id comparison
        const isOwnMessage = currentUserRole 
          ? message.sender_role === currentUserRole
          : message.user_id === currentUserId;
        
        return (
          <MessageBubble
            key={message.id}
            message={message}
            isOwnMessage={isOwnMessage}
            onDelete={onDeleteMessage}
          />
        );
      })}
      <div ref={messagesEndRef} />
    </div>
  );
}
